/**
 * ==========================================================================
 * MAIN JAVASCRIPT FILE
 * ==========================================================================
 * 
 * Table of Contents:
 * 
 * 1. Header & Navigation
 *    - Side Menu Toggle
 *    - Header Blur on Scroll
 *    - Header Marquee Animation
 * 
 * 2. Banner Section
 *    - Banner Top Swiper
 *    - Banner Carousel Grid
 *    - Quote/Testimonial Cards
 * 
 * 3. Milestone Section
 *    - SVG Path Animation
 *    - Milestone Dots & Labels
 * 
 * 4. Swiper Sliders
 *    - Be Part Swiper
 *    - About Swiper
 *    - News Swiper
 *    - Podcast Swiper
 *    - Gallery Swiper
 *    - Read More Swiper
 * 
 * 5. Interactive Components
 *    - Funding Counter Animation
 *    - Learn More Toggle
 *    - Footer Accordion
 * 
 * 6. E-commerce Features
 *    - Cart Drawer
 *    - AJAX Add to Cart
 * 
 * 7. Search Modal
 * 
 * 8. Utilities
 *    - Newsletter Form Handler
 *    - Scroll to Element
 * 
 * ==========================================================================
 * @version 1.0.0
 * @requires Swiper.js
 * ==========================================================================
 */

(function () {
	'use strict';

	/* ==========================================================================
	    Announcement bar Counter Animation
	   ========================================================================== */

	/**
	 * 5.1 Funding Counter Animation
	 * Animates counter from 0 to target value
	 * + opacity from 0 to 1
	 */

	const HeaderFundingCounter = {
		selector: '#header-funding-counter',
		duration: 2000, // milliseconds
		hasRun: false,

		init() {
			const counter = document.querySelector(this.selector);
			if (!counter) return;

			// Set initial state
			counter.textContent = '0';
			counter.style.opacity = 0;

			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach(entry => {
						if (entry.isIntersecting && !this.hasRun) {
							this.hasRun = true;
							this.startCounter(counter);
							observer.disconnect(); // run once only
						}
					});
				},
				{
					threshold: 0.4 // trigger when 40% visible
				}
			);

			observer.observe(counter);
		},

		startCounter(counter) {
			const targetValue = counter.getAttribute('data-target') || '1600505';
			const target = parseInt(targetValue.replace(/,/g, ''), 10);
			const goal = 10000000;

			const intervalTime = 20;
			const steps = this.duration / intervalTime;
			const increment = target / steps;

			let current = 0;

			const timer = setInterval(() => {
				current += increment;

				if (current >= target) {
					current = target;
					clearInterval(timer);
				}

				// Update number
				counter.textContent = Math.floor(current).toLocaleString();

				// Opacity sync (0 → 1)
				const progress = current / target;
				counter.style.opacity = Math.min(progress, 1);

			}, intervalTime);

			// Progress bar (optional)
			const progressBar = document.getElementById('ls-progress-bar');
			if (progressBar) {
				const progress = (target / goal) * 100;
				setTimeout(() => {
					progressBar.style.width = `${progress}%`;
				}, 300);
			}
		}
	};

	/* ==========================================================================
	   1. HEADER & NAVIGATION
	   ========================================================================== */

	/**
	 * 1.1 Side Menu Toggle
	 * Handles mobile navigation drawer open/close functionality
	 */
	const HeaderSideMenu = {
		selectors: {
			hamburger: '.navigation-toggle',
			sideDrawer: '.side-drawer',
			closeBtn: '.side-drawer-close',
			dropdowns: '.side-dropdown > a',
			body: 'body'
		},

		/**
		 * Initialize side menu functionality
		 */
		init() {
			const hamburger = document.querySelector(this.selectors.hamburger);
			const sideDrawer = document.querySelector(this.selectors.sideDrawer);
			const closeBtn = document.querySelector(this.selectors.closeBtn);
			const dropdowns = document.querySelectorAll(this.selectors.dropdowns);

			if (!hamburger || !sideDrawer) return;

			// Open drawer
			hamburger.addEventListener('click', () => {
				sideDrawer.classList.add('active');
				document.body.classList.add('overflow-hidden');
			});

			// Close drawer
			if (closeBtn) {
				closeBtn.addEventListener('click', () => {
					sideDrawer.classList.remove('active');
					document.body.classList.remove('overflow-hidden');
				});
			}

			// Toggle dropdowns
			dropdowns.forEach((drop) => {
				drop.addEventListener('click', (e) => {
					e.preventDefault();
					drop.parentElement.classList.toggle('active');
				});
			});
		}
	};

	/**
	 * 1.2 Header Blur on Scroll
	 * Adds blur effect to header when user scrolls down
	 */
	const HeaderBlur = {
		selectors: {
			header: '.header'
		},
		scrollThreshold: 10,

		/**
		 * Initialize header blur effect
		 */
		init() {
			const header = document.querySelector(this.selectors.header);
			if (!header) return;

			window.addEventListener('scroll', () => {
				if (window.scrollY > this.scrollThreshold) {
					header.classList.add('is-blurred');
				} else {
					header.classList.remove('is-blurred');
				}
			});
		}
	};

	/**
	 * 1.3 Header Marquee Animation
	 * Creates seamless infinite scroll animation for header top bar on mobile
	 */
	const HeaderMarquee = {
		selectors: {
			headerTop: '.header-top',
			headerTrack: '.header-top-track'
		},
		mobileBreakpoint: 1024,
		speed: 30, // pixels per second

		/**
		 * Setup marquee animation
		 */
		setup() {
			const headerTop = document.querySelector(this.selectors.headerTop);
			const headerTrack = document.querySelector(this.selectors.headerTrack);

			if (!headerTop || !headerTrack) return;

			const isMobile = window.matchMedia(`(max-width: ${this.mobileBreakpoint}px)`).matches;

			// Save original content only once
			if (!headerTrack.dataset.originalContent) {
				headerTrack.dataset.originalContent = headerTrack.innerHTML.trim();
			}

			const originalContent = headerTrack.dataset.originalContent;

			if (isMobile) {
				// Prevent double duplication
				if (headerTrack.dataset.duplicated === 'true') return;

				// Duplicate content for smooth infinite scroll
				headerTrack.innerHTML = originalContent.repeat(4);
				headerTrack.dataset.duplicated = 'true';

				// Force DOM reflow
				void headerTrack.offsetHeight;

				// Calculate animation duration
				const totalWidth = headerTrack.scrollWidth;
				const duration = totalWidth / this.speed;

				headerTrack.style.setProperty('--marquee-duration', `${duration}s`);
				headerTrack.style.animation = `marqueeLoop ${duration}s linear infinite`;
			} else {
				// Reset to original for desktop
				if (headerTrack.dataset.duplicated === 'true') {
					headerTrack.innerHTML = originalContent;
					headerTrack.dataset.duplicated = 'false';
					headerTrack.style.animation = '';
					headerTrack.style.removeProperty('--marquee-duration');
				}
			}
		},

		/**
		 * Initialize marquee with resize handling
		 */
		init() {
			// Initial setup
			this.setup();

			// Setup on window load (fallback)
			window.addEventListener('load', () => {
				setTimeout(() => this.setup(), 100);
			});

			// Handle resize with debounce
			let resizeTimer;
			window.addEventListener('resize', () => {
				clearTimeout(resizeTimer);
				resizeTimer = setTimeout(() => {
					const track = document.querySelector(this.selectors.headerTrack);
					if (track) track.dataset.duplicated = 'false';
					this.setup();
				}, 200);
			});
		}
	};

	/* ==========================================================================
	   2. BANNER SECTION
	   ========================================================================== */

	/**
	 * 2.1 Banner Top Swiper
	 * Initializes auto-playing swiper for banner top slider
	 */
	const BannerTopSwiper = {
		selector: '.banner-top-swiper',

		/**
		 * Initialize swiper instances
		 * @param {HTMLElement|null} section - Optional section to scope initialization
		 */
		init(section = null) {
			const sliders = section
				? section.querySelectorAll(this.selector)
				: document.querySelectorAll(this.selector);

			sliders.forEach((el) => {
				// Destroy old swiper if exists
				if (el.swiper) {
					el.swiper.destroy(true, true);
				}

				new Swiper(el, {
					slidesPerView: 'auto',
					centeredSlides: true,
					spaceBetween: 0,
					loop: true,
					speed: 2200,
					autoplay: {
						delay: 2000,
						disableOnInteraction: false
					},
					allowTouchMove: false
				});
			});
		}
	};

	/**
	 * 2.2 Banner Carousel Grid
	 * Creates dynamic column-based image carousel with hover effects
	 */
	const BannerCarousel = {
		selectors: {
			carousel: '#ls-banner-carousel-wrapper',
			imagesContainer: '#ls-banner-carousel-images-container',
			testimonialBox: '#ls-banner-carousel-box'
		},
		originalItems: [],
		resizeTimer: null,

		/**
		 * Get column count based on viewport width
		 * @returns {number} Number of columns to display
		 */
		getColumnCount() {
			const width = window.innerWidth;
			if (width <= 768) return 3;
			if (width <= 1024) return 5;
			return 11;
		},

		/**
		 * Check if device is mobile
		 * @returns {boolean}
		 */
		isMobile() {
			return window.innerWidth <= 768;
		},

		/**
		 * Create carousel columns and populate with items
		 */
		createCarousel() {
			const carousel = document.querySelector(this.selectors.carousel);
			const imagesContainer = document.querySelector(this.selectors.imagesContainer);

			if (!carousel || !imagesContainer) return;

			// Cache original items once
			this.originalItems = Array.from(
				imagesContainer.querySelectorAll('.banner-carousel-item')
			);

			if (!this.originalItems.length) return;

			// Clear carousel before rebuild
			carousel.innerHTML = '';

			const columnCount = this.getColumnCount();
			const itemsPerColumn = Math.ceil(this.originalItems.length / columnCount);

			for (let i = 0; i < columnCount; i++) {
				const column = document.createElement('div');
				column.className = 'banner-carousel-column';
				column.dataset.columnIndex = i;

				const track = document.createElement('div');
				track.className = 'banner-carousel-track';

				const columnItems = [];
				for (let j = 0; j < itemsPerColumn; j++) {
					const index = (i + j * columnCount) % this.originalItems.length;
					columnItems.push(this.originalItems[index]);
				}

				// Clone twice for seamless loop
				[...columnItems, ...columnItems].forEach(item => {
					track.appendChild(item.cloneNode(true));
				});

				column.appendChild(track);
				carousel.appendChild(column);
			}

			this.initHoverQuoteCards();
		},

		/**
		 * Initialize hover effects for quote/testimonial cards
		 */
		initHoverQuoteCards() {
			const carousel = document.querySelector(this.selectors.carousel);
			const testimonialBox = document.querySelector(this.selectors.testimonialBox);

			if (!carousel || !testimonialBox) return;

			let pausedColumn = null;
			let currentItem = null;

			const closeBox = () => {
				testimonialBox.classList.remove('active', 'scale-in');

				if (pausedColumn) {
					const track = pausedColumn.querySelector('.banner-carousel-track');
					if (track) track.style.animationPlayState = 'running';
					pausedColumn = null;
				}

				currentItem = null;
			};

			carousel.querySelectorAll('.banner-carousel-item').forEach(item => {

				item.addEventListener('mouseenter', () => {
					if (this.isMobile()) return;

					const column = item.closest('.banner-carousel-column');
					const track = column.querySelector('.banner-carousel-track');

					if (pausedColumn && pausedColumn !== column) {
						pausedColumn.querySelector('.banner-carousel-track').style.animationPlayState = 'running';
					}

					pausedColumn = column;
					track.style.animationPlayState = 'paused';

					// 🔹 Update testimonial content
					const textEl = testimonialBox.querySelector('.banner-carousel-box-text');
					const nameEl = testimonialBox.querySelector('.banner-box-bottom-name');
					const subtitleEl = testimonialBox.querySelector('.banner-box-bottom-subtitle');
					const imgEl = testimonialBox.querySelector('.banner-box-bottom-image');

					if (textEl) textEl.textContent = item.dataset.testimonialText || '';
					if (nameEl) nameEl.textContent = item.dataset.testimonialName || '';
					if (subtitleEl) subtitleEl.textContent = item.dataset.testimonialSubtitle || '';
					if (imgEl && item.dataset.testimonialImage) imgEl.src = item.dataset.testimonialImage;

					// 🔥 POSITION CALCULATION (MAIN PART)
					const itemRect = item.getBoundingClientRect();
					const carouselRect = carousel.getBoundingClientRect();

					const top =
						itemRect.top - carouselRect.top - testimonialBox.offsetHeight - 12;

					const left =
						itemRect.left - carouselRect.left +
						itemRect.width / 2 -
						testimonialBox.offsetWidth / 2;

					testimonialBox.style.top = `${top}px`;
					testimonialBox.style.left = `${left}px`;

					// 🔥 CONDITIONAL MARGIN LOGIC
					if (left > 1200) {
					testimonialBox.style.marginLeft = '0px';
					} else {
					testimonialBox.style.marginLeft = '300px';
					}

					testimonialBox.classList.add('active');
					requestAnimationFrame(() => testimonialBox.classList.add('scale-in'));

					currentItem = item;
				});

				// Mouse enter - show testimonial
				// item.addEventListener('mouseenter', () => {
				//   if (this.isMobile()) return;

				//   const column = item.closest('.banner-carousel-column');
				//   const track = column.querySelector('.banner-carousel-track');

				//   if (pausedColumn && pausedColumn !== column) {
				//     pausedColumn.querySelector('.banner-carousel-track').style.animationPlayState = 'running';
				//   }

				//   pausedColumn = column;
				//   track.style.animationPlayState = 'paused';

				//   // Update testimonial content
				//   const textEl = testimonialBox.querySelector('.banner-carousel-box-text');
				//   const nameEl = testimonialBox.querySelector('.banner-box-bottom-name');
				//   const subtitleEl = testimonialBox.querySelector('.banner-box-bottom-subtitle');
				//   const imgEl = testimonialBox.querySelector('.banner-box-bottom-image');

				//   if (textEl) textEl.textContent = item.dataset.testimonialText || '';
				//   if (nameEl) nameEl.textContent = item.dataset.testimonialName || '';
				//   if (subtitleEl) subtitleEl.textContent = item.dataset.testimonialSubtitle || '';
				//   if (imgEl && item.dataset.testimonialImage) imgEl.src = item.dataset.testimonialImage;

				//   testimonialBox.classList.add('active');
				//   requestAnimationFrame(() => testimonialBox.classList.add('scale-in'));

				//   currentItem = item;
				// });

				// Mouse leave - hide testimonial
				item.addEventListener('mouseleave', () => {
					if (!this.isMobile() && currentItem === item) {
						closeBox();
					}
				});

				// Click handler for mobile
				item.addEventListener('click', (e) => {
					if (!this.isMobile()) return;
					e.stopPropagation();
				});
			});

			// Close on document click or escape
			document.addEventListener('click', closeBox);
			document.addEventListener('keydown', (e) => {
				if (e.key === 'Escape') closeBox();
			});
		},

		/**
		 * Initialize banner carousel
		 */
		init() {
			this.createCarousel();

			// Handle resize with debounce
			window.addEventListener('resize', () => {
				clearTimeout(this.resizeTimer);
				this.resizeTimer = setTimeout(() => this.createCarousel(), 250);
			});
		}
	};

	/* ==========================================================================
	   3. MILESTONE SECTION
	   ========================================================================== */

	/**
	 * 3.1 Milestone SVG Animation
	 * Handles scroll-based SVG path animation with milestone markers
	 */
	const MilestoneAnimation = {
		init() {

			const sec = document.querySelector('.milestone');
			if (!sec) return;

			const path = sec.querySelector('#line'); // ✅ your fixed ID
			const group = sec.querySelector('#ls-milestones-group');
			const wrapper = sec.querySelector('.mil-l-w');
			const p1 = sec.querySelector('.text');
			const p2 = sec.querySelector('.bottom');

			if (!path || !group || !wrapper) return;
			if (typeof gsap === "undefined") return;

			gsap.registerPlugin(ScrollTrigger);

			const pathLength = path.getTotalLength();

			/* ----------------------------------
			CREATE LABELS + DOTS
			----------------------------------- */

			const data = window.milestoneData || [
				{ title: 'Kickoff', desc: 'The journey begins', percentage: 0 },
				{ title: 'Planning', desc: 'Roadmap creation', percentage: 30 },
				{ title: 'Design', desc: 'Wireframes & mockups', percentage: 60 },
				{ title: 'Launch', desc: 'Product goes live', percentage: 100 }
			];

			const milestones = data.map((item) => {

				const fraction = item.percentage / 100;
				const point = path.getPointAtLength(pathLength * fraction);

				const cx = point.x;
				const cy = point.y;

				// DOT
				const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
				circle.setAttribute('class', 'milestone-dot');
				circle.setAttribute('cx', cx);
				circle.setAttribute('cy', cy);
				group.appendChild(circle);

				// LABEL GROUP
				const labelGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
				labelGroup.setAttribute('class', 'milestone-label');

				const title = document.createElementNS('http://www.w3.org/2000/svg', 'text');
				title.setAttribute('x', cx + 20);
				title.setAttribute('y', cy + 60);
				title.textContent = item.title;

				const foreignObject = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
				foreignObject.setAttribute('x', cx + 20);
				foreignObject.setAttribute('y', cy + 70);
				foreignObject.setAttribute('width', '260');
				foreignObject.setAttribute('height', '150');

				const descDiv = document.createElement('div');
				descDiv.className = 'milestone-description';
				descDiv.innerHTML = item.desc || '';

				if (item.button_text && item.button_link) {
					const button = document.createElement('a');
					button.href = item.button_link;
					button.textContent = item.button_text;
					button.className = 'milestone-button button-primary';
					descDiv.appendChild(button);
				}

				foreignObject.appendChild(descDiv);
				labelGroup.appendChild(title);
				labelGroup.appendChild(foreignObject);

				group.appendChild(labelGroup);

				return { fraction, circle, labelGroup };
			});

			/* ----------------------------------
			GSAP ANIMATION
			----------------------------------- */

			gsap.set(path, {
				strokeDasharray: pathLength,
				strokeDashoffset: pathLength
			});

			// hide it first (nothing drawn)
			gsap.set(path, { drawSVG: '0% 0%' })
			gsap.set(sec, { height: `120vh` })

			const lineHeight = wrapper.offsetHeight;
			const lineShift = lineHeight - window.innerHeight / 4;

			const tl = gsap.timeline({
				defaults: { ease: 'none', duration: 1 }
			});

			// Fade out intro text
			if (p1 && p2) {
				tl.to([p1, p2], {
					autoAlpha: 0,
					duration: 0.07
				}, 0);
			}

			tl.to(path, { strokeDashoffset: 0 }, 0)
			.to(wrapper, { y: -lineShift }, 0);

			ScrollTrigger.create({
				trigger: sec,
				start: "top top",
				end: "+=300%",
				pin: true,
				scrub: 0,
				animation: tl,
				onUpdate: self => {
					let activeIndex = 0;

					milestones.forEach((m, i) => {
						if (self.progress >= m.fraction) activeIndex = i;
					});

					milestones.forEach((m, i) => {
						m.circle.classList.toggle('active', i === activeIndex);
						m.labelGroup.classList.toggle('active', i === activeIndex);
					});
				}
			});
		}
	};



	/* ==========================================================================
	   4. SWIPER SLIDERS
	   ========================================================================== */

	/**
	 * 4.1 Be Part Section Swiper
	 */
	const BePartSwiper = {
		selector: '.be-part-swiper',

		/**
		 * Initialize swiper
		 * @param {HTMLElement|null} section - Optional section scope
		 */
		init(section = null) {
			const swipers = section
				? section.querySelectorAll(this.selector)
				: document.querySelectorAll(this.selector);

			swipers.forEach((el) => {
				if (el.swiper) return; // Prevent double init

				new Swiper(el, {
					slidesPerView: 1,
					loop: true,
					speed: 3000,
					effect: 'fade',
					fadeEffect: {
						crossFade: true
					},
					autoHeight: true,
					autoplay: {
						delay: 3000,
						disableOnInteraction: false
					},
					pagination: {
						el: el.querySelector('.swiper-pagination'),
						clickable: true
					}
				});
			});
		}
	};

	/**
	 * 4.2 About Section Swiper
	 */
	const AboutSwiper = {
		selector: '.about-swiper',

		/**
		 * Update slide counter
		 * @param {Swiper} swiper - Swiper instance
		 * @param {HTMLElement} section - Parent section
		 */
		updateCounter(swiper, section) {
			if (!section) return;

			const currentEl = section.querySelector('.about-slider-counter .current');
			const totalEl = section.querySelector('.about-slider-counter .total');

			if (!currentEl || !totalEl) return;

			currentEl.textContent = swiper.realIndex + 1;
			totalEl.textContent = swiper.slides.filter(slide => !slide.classList.contains('swiper-slide-duplicate')).length;
		},

		/**
		 * Initialize swiper
		 */
		init() {
			document.querySelectorAll(this.selector).forEach((slider) => {
				if (slider.swiper) return;

				const section = slider.closest('.mission-section');

				new Swiper(slider, {
					slidesPerView: 1,
					effect: 'fade',
					fadeEffect: { crossFade: true },
					speed: 500,
					loop: true,
					autoplay: false,
					navigation: {
						nextEl: section?.querySelector('.about-swiper-next'),
						prevEl: section?.querySelector('.about-swiper-prev')
					},
					on: {
						init: (swiper) => this.updateCounter(swiper, section),
						slideChange: (swiper) => this.updateCounter(swiper, section)
					}
				});
			});
		}
	};

	/**
	 * 4.3 News Section Swiper
	 */
	const NewsSwiper = {
		selector: '.news-swiper',

		/**
		 * Initialize swiper
		 */
		init() {
			document.querySelectorAll(this.selector).forEach((slider) => {
				if (slider.swiper) return;

				const section = slider.closest('.news-section');

				new Swiper(slider, {
					slidesPerView: 'auto',
					spaceBetween: 0,
					loop: true,
					speed: 2200,
					autoplay: {
						delay: 3000,
						disableOnInteraction: false
					},
					pagination: {
						el: section?.querySelector('.swiper-pagination'),
						clickable: true
					},
					navigation: {
						nextEl: section?.querySelector('.news-swiper-next'),
						prevEl: section?.querySelector('.news-swiper-prev')
					},
					breakpoints: {
						0: { slidesPerView: 1 },
						576: { slidesPerView: 1 },
						992: { slidesPerView: 'auto' }
					}
				});
			});
		}
	};

	/**
	 * 4.4 Podcast Section Swiper
	 */
	const PodcastSwiper = {
		selector: '.podcast-swiper',

		/**
		 * Initialize swiper
		 */
		init() {
			document.querySelectorAll(this.selector).forEach((slider) => {
				if (slider.classList.contains('swiper-initialized')) return;

				const section = slider.closest('.podcast-swiper');
				if (!section) return;

				new Swiper(slider, {
					slidesPerView: 3,
					spaceBetween: 32,
					loop: true,
					speed: 600,
					autoplay: {
						delay: 3000,
						disableOnInteraction: false
					},
					pagination: {
						el: section.querySelector('.swiper-pagination'),
						clickable: true
					},
					navigation: {
						nextEl: section.querySelector('.podcast-next'),
						prevEl: section.querySelector('.podcast-prev')
					},
					breakpoints: {
						0: { slidesPerView: 1 },
						576: { slidesPerView: 1 },
						992: { slidesPerView: 2 },
						1200: { slidesPerView: 3 }
					}
				});
			});
		}
	};

	/**
	 * 4.5 Gallery Section Swiper (Mobile Only)
	 */
	const GallerySwiper = {
		selector: '.gallery-section',
		mobileBreakpoint: 768,

		/**
		 * Initialize swiper
		 * @param {Document|HTMLElement} context - Context to search within
		 */
		init(context = document) {
			context.querySelectorAll(this.selector).forEach(section => {
				const slider = section.querySelector('.js-gallery-slider');
				if (!slider || typeof Swiper === 'undefined') return;

				const swiper = slider.swiper;

				if (window.innerWidth < this.mobileBreakpoint) {
					if (!swiper) {
						new Swiper(slider, {
							slidesPerView: 1,
							spaceBetween: 0,
							loop: false,
							autoHeight: true,
							pagination: {
								el: slider.querySelector('.swiper-pagination'),
								clickable: true
							}
						});
					}
				} else {
					if (swiper) {
						swiper.destroy(true, true);
					}
				}
			});
		}
	};

	/**
	 * 4.6 Read More Section Swiper
	 */
	const ReadMoreSwiper = {
		selector: '.read-more-section',

		/**
		 * Initialize swiper
		 */
		init() {
			document.querySelectorAll(this.selector).forEach((section) => {
				const sliderEl = section.querySelector('.read-more-slide');
				const paginationEl = section.querySelector('.swiper-pagination');
				const prevEl = section.querySelector('.swiper-button-prev');
				const nextEl = section.querySelector('.swiper-button-next');

				if (!sliderEl || sliderEl.classList.contains('swiper-initialized')) return;

				sliderEl.classList.add('swiper');

				// Create wrapper
				const wrapper = document.createElement('div');
				wrapper.className = 'swiper-wrapper';

				[...sliderEl.children].forEach((child) => {
					child.classList.add('swiper-slide');
					wrapper.appendChild(child);
				});

				sliderEl.appendChild(wrapper);

				new Swiper(sliderEl, {
					slidesPerView: 1,
					spaceBetween: 20,
					speed: 600,
					loop: false,
					navigation: {
						nextEl: nextEl,
						prevEl: prevEl
					},
					pagination: {
						el: paginationEl,
						clickable: true
					},
					breakpoints: {
						768: { slidesPerView: 2 },
						1024: { slidesPerView: 3 }
					}
				});
			});
		}
	};

	/* ==========================================================================
	   5. INTERACTIVE COMPONENTS
	   ========================================================================== */

	/**
	 * 5.1 Funding Counter Animation
	 * Animates counter from 0 to target value
	 * + opacity from 0 to 1
	 */

	const FundingCounter = {
		selector: '#ls-funding-counter',
		duration: 2000, // milliseconds
		hasRun: false,

		init() {
			const counter = document.querySelector(this.selector);
			if (!counter) return;

			// Set initial state
			counter.textContent = '0';
			counter.style.opacity = 0;

			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach(entry => {
						if (entry.isIntersecting && !this.hasRun) {
							this.hasRun = true;
							this.startCounter(counter);
							observer.disconnect(); // run once only
						}
					});
				},
				{
					threshold: 0.4 // trigger when 40% visible
				}
			);

			observer.observe(counter);
		},

		startCounter(counter) {
			const targetValue = counter.getAttribute('data-target') || '1600505';
			const target = parseInt(targetValue.replace(/,/g, ''), 10);
			const goal = 10000000;

			const intervalTime = 20;
			const steps = this.duration / intervalTime;
			const increment = target / steps;

			let current = 0;

			const timer = setInterval(() => {
				current += increment;

				if (current >= target) {
					current = target;
					clearInterval(timer);
				}

				// Update number
				counter.textContent = Math.floor(current).toLocaleString();

				// Opacity sync (0 → 1)
				const progress = current / target;
				counter.style.opacity = Math.min(progress, 1);

			}, intervalTime);

			// Progress bar (optional)
			const progressBar = document.getElementById('ls-progress-bar');
			if (progressBar) {
				const progress = (target / goal) * 100;
				setTimeout(() => {
					progressBar.style.width = `${progress}%`;
				}, 300);
			}
		}
	};

	/**
	 * 5.2 Learn More Toggle
	 * Expands/collapses text content
	 */
	const LearnMoreToggle = {
		selector: '.learn-more-toggle',

		/**
		 * Initialize toggle functionality
		 */
		init() {
			const toggles = document.querySelectorAll(this.selector);

			toggles.forEach(toggle => {
				if (toggle.dataset.initialized === 'true') return;

				toggle.addEventListener('click', function (e) {
					e.preventDefault();

					const paragraph = this.closest('.text');
					if (!paragraph) return;

					const contentMore = paragraph.querySelector('.content-more');
					const contentDots = paragraph.querySelector('.content-dots');

					if (contentMore && contentDots) {
						const isExpanded = contentMore.style.display !== 'none';

						if (isExpanded) {
							contentMore.style.display = 'none';
							contentDots.style.display = 'inline';
							this.textContent = 'Learn more';
						} else {
							contentMore.style.display = 'inline';
							contentDots.style.display = 'none';
							this.textContent = 'Show less';
						}
					}
				});

				toggle.dataset.initialized = 'true';
			});
		}
	};

	/**
	 * 5.3 Footer Accordion (Mobile)
	 * Handles collapsible footer navigation on mobile
	 */
	const FooterAccordion = {
		selector: '.footer-link',
		mobileBreakpoint: 576,

		/**
		 * Setup accordion functionality
		 */
		setup() {
			const isMobile = window.matchMedia(`(max-width: ${this.mobileBreakpoint}px)`).matches;
			const links = document.querySelectorAll(this.selector);

			if (!isMobile) {
				// Remove all active states when switching to desktop
				links.forEach(item => item.classList.remove('active'));
				return;
			}

			document.querySelectorAll(`${this.selector} h6`).forEach(title => {
				title.onclick = () => {
					const parent = title.parentElement;

					// Close others
					links.forEach(item => {
						if (item !== parent) item.classList.remove('active');
					});

					parent.classList.toggle('active');
				};
			});
		},

		/**
		 * Initialize accordion
		 */
		init() {
			this.setup();
			window.addEventListener('resize', () => this.setup());
		}
	};

	/* ==========================================================================
	   6. E-COMMERCE FEATURES
	   ========================================================================== */

	/**
	 * 6.1 Cart Drawer
	 * Handles cart drawer functionality for Shopify
	 */
	const CartDrawer = {
		selectors: {
			drawer: '#ls-cart-drawer',
			icon: '#ls-cart-icon',
			closeBtn: '.cart-drawer-close',
			overlay: '.cart-drawer-overlay',
			count: '#ls-cart-count',
			body: '#ls-cart-drawer-body'
		},

		/**
		 * Format money value
		 * @param {number} cents - Price in cents
		 * @returns {string} Formatted price
		 */
		formatMoney(cents) {
			return new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD'
			}).format(cents / 100);
		},

		/**
		 * Open cart drawer
		 */
		open() {
			const drawer = document.querySelector(this.selectors.drawer);
			if (!drawer) return;

			drawer.classList.add('active');
			document.body.style.overflow = 'hidden';
			this.update();
		},

		/**
		 * Close cart drawer
		 */
		close() {
			const drawer = document.querySelector(this.selectors.drawer);
			if (!drawer) return;

			drawer.classList.remove('active');
			document.body.style.overflow = '';
		},

		/**
		 * Update cart count badge
		 * @param {number} count - Item count
		 */
		updateCount(count) {
			const countEl = document.querySelector(this.selectors.count);
			if (!countEl) return;

			if (count > 0) {
				countEl.textContent = count;
				countEl.style.display = 'inline-block';
			} else {
				countEl.style.display = 'none';
			}
		},

		/**
		 * Update cart item quantity
		 * @param {string} key - Item key
		 * @param {number} quantity - New quantity
		 */
		updateItem(key, quantity) {
			fetch('/cart/change.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ id: key, quantity: quantity })
			})
				.then(response => response.json())
				.then(cart => {
					this.updateItems(cart);
					this.updateCount(cart.item_count);
				})
				.catch(error => console.error('Error updating cart:', error));
		},

		/**
		 * Attach event listeners to cart items
		 */
		attachItemListeners() {
			// Quantity buttons
			document.querySelectorAll('.cart-item-quantity-btn').forEach(btn => {
				btn.addEventListener('click', () => {
					const action = btn.dataset.action;
					const key = btn.dataset.key;
					const input = document.querySelector(`.cart-item-quantity-input[data-key="${key}"]`);
					let quantity = parseInt(input.value) || 0;

					if (action === 'increase') quantity++;
					else if (action === 'decrease' && quantity > 0) quantity--;

					this.updateItem(key, quantity);
				});
			});

			// Remove buttons
			document.querySelectorAll('.cart-item-remove').forEach(btn => {
				btn.addEventListener('click', () => {
					this.updateItem(btn.dataset.key, 0);
				});
			});
		},

		/**
		 * Update cart items in drawer
		 * @param {Object} cart - Cart data from Shopify
		 */
		updateItems(cart) {
			const cartBody = document.querySelector(this.selectors.body);
			if (!cartBody) return;

			if (cart.item_count === 0) {
				cartBody.innerHTML = `
          <div class="cart-drawer-empty">
            <p>Your cart is empty</p>
            <a href="/collections/all" class="button-primary">Continue Shopping</a>
          </div>
        `;
				return;
			}

			let itemsHTML = '<div class="cart-items">';
			cart.items.forEach((item, index) => {
				itemsHTML += `
          <div class="cart-item" data-line-item="${index + 1}">
            <div class="cart-item-image">
              <img src="${item.image}" alt="${item.title}" loading="lazy">
            </div>
            <div class="cart-item-details">
              <h3 class="cart-item-title">
                <a href="${item.url}">${item.product_title}</a>
              </h3>
              ${item.variant_title ? `<p class="cart-item-variant">${item.variant_title}</p>` : ''}
              <div class="cart-item-price">${this.formatMoney(item.final_line_price)}</div>
            </div>
            <div class="cart-item-quantity">
              <button type="button" class="cart-item-quantity-btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease quantity">-</button>
              <input type="number" value="${item.quantity}" min="0" class="cart-item-quantity-input" data-key="${item.key}">
              <button type="button" class="cart-item-quantity-btn" data-action="increase" data-key="${item.key}" aria-label="Increase quantity">+</button>
            </div>
            <button class="cart-item-remove" data-key="${item.key}" aria-label="Remove item">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="#351E1B" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        `;
			});
			itemsHTML += '</div>';

			itemsHTML += `
        <div class="cart-drawer-footer">
          <div class="cart-drawer-subtotal">
            <span>Subtotal</span>
            <span class="cart-drawer-total">${this.formatMoney(cart.total_price)}</span>
          </div>
          <p class="cart-drawer-note">Taxes and shipping calculated at checkout</p>
          <a href="/cart" class="button-outline">View Cart</a>
          <form action="/cart" method="post" enctype="multipart/form-data">
            <button type="submit" name="checkout" class="button-primary">Checkout</button>
          </form>
        </div>
      `;

			cartBody.innerHTML = itemsHTML;
			this.attachItemListeners();
		},

		/**
		 * Fetch and update cart drawer content
		 */
		update() {
			fetch('/cart.js')
				.then(response => response.json())
				.then(cart => {
					this.updateCount(cart.item_count);
					this.updateItems(cart);
				})
				.catch(error => console.error('Error fetching cart:', error));
		},

		/**
		 * Initialize cart drawer
		 */
		init() {
			const icon = document.querySelector(this.selectors.icon);
			const closeBtn = document.querySelector(this.selectors.closeBtn);
			const overlay = document.querySelector(this.selectors.overlay);

			if (icon) icon.addEventListener('click', () => this.open());
			if (closeBtn) closeBtn.addEventListener('click', () => this.close());
			if (overlay) overlay.addEventListener('click', () => this.close());

			// Make functions globally available
			window.openCartDrawer = () => this.open();
			window.updateCartDrawer = () => this.update();
		}
	};

	/**
	 * 6.2 AJAX Add to Cart
	 * Handles form submissions for adding products to cart
	 */
	const AjaxAddToCart = {
		selector: '.ajax-add-to-cart',

		/**
		 * Initialize AJAX add to cart
		 */
		init() {
			document.querySelectorAll(this.selector).forEach(form => {
				form.addEventListener('submit', function (e) {
					e.preventDefault();

					const formData = new FormData(this);

					fetch('/cart/add.js', {
						method: 'POST',
						body: formData
					})
						.then(response => response.json())
						.then(() => {
							if (window.updateCartDrawer) window.updateCartDrawer();
							if (window.openCartDrawer) window.openCartDrawer();
						})
						.catch(error => {
							console.error('Error:', error);
							alert('There was an error adding the product to cart.');
						});
				});
			});
		}
	};

	/* ==========================================================================
	   7. SEARCH MODAL
	   ========================================================================== */

	/**
	 * Search Modal
	 * Handles search modal open/close functionality
	 */
	const SearchModal = {
		selectors: {
			modal: '#ls-search-modal',
			icon: 'a[href*="/search"]',
			closeBtn: '.search-modal-close',
			overlay: '.search-modal-overlay',
			input: '#ls-search-modal-input',
			form: '.search-modal-form'
		},

		/**
		 * Open search modal
		 */
		open() {
			const modal = document.querySelector(this.selectors.modal);
			const input = document.querySelector(this.selectors.input);

			if (!modal) return;

			modal.classList.add('active');
			document.body.style.overflow = 'hidden';

			if (input) {
				setTimeout(() => input.focus(), 100);
			}
		},

		/**
		 * Close search modal
		 */
		close() {
			const modal = document.querySelector(this.selectors.modal);
			if (!modal) return;

			modal.classList.remove('active');
			document.body.style.overflow = '';
		},

		/**
		 * Initialize search modal
		 */
		init() {
			const modal = document.querySelector(this.selectors.modal);
			const icon = document.querySelector(this.selectors.icon);
			const closeBtn = document.querySelector(this.selectors.closeBtn);
			const overlay = document.querySelector(this.selectors.overlay);
			const form = document.querySelector(this.selectors.form);

			if (!modal || !icon) return;

			icon.addEventListener('click', (e) => {
				e.preventDefault();
				this.open();
			});

			if (closeBtn) closeBtn.addEventListener('click', () => this.close());
			if (overlay) overlay.addEventListener('click', () => this.close());

			document.addEventListener('keydown', (e) => {
				if (e.key === 'Escape' && modal.classList.contains('active')) {
					this.close();
				}
			});

			if (form) {
				form.addEventListener('submit', () => this.close());
			}
		}
	};

	/* ==========================================================================
	   8. UTILITIES
	   ========================================================================== */

	/**
	 * 8.1 Newsletter Message Handler
	 * Auto-hides success/error messages
	 */
	const NewsletterMessage = {
		selector: '.newsletter-success, .newsletter-error',
		timeout: 5000,

		/**
		 * Initialize message handler
		 */
		init() {
			const msg = document.querySelector(this.selector);
			if (msg) {
				setTimeout(() => {
					msg.style.display = 'none';
				}, this.timeout);
			}
		}
	};

	/**
	 * 8.2 Scroll to Element
	 * Handles smooth scrolling to specific element based on URL hash
	 */
	const ScrollToElement = {
		targetSelector: '.footer-top',
		requiredHash: '#newsletter-form-top',
		blockedKeyword: 'challenge',
		offset: 0,

		/**
		 * Wait for element to exist
		 * @param {string} selector - CSS selector
		 * @param {number} timeout - Timeout in milliseconds
		 * @returns {Promise<HTMLElement>}
		 */
		waitForElement(selector, timeout = 5000) {
			return new Promise((resolve, reject) => {
				const el = document.querySelector(selector);
				if (el) return resolve(el);

				const observer = new MutationObserver(() => {
					const found = document.querySelector(selector);
					if (found) {
						observer.disconnect();
						clearTimeout(timer);
						return resolve(found);
					}
				});

				observer.observe(document.documentElement || document.body, {
					childList: true,
					subtree: true
				});

				const timer = setTimeout(() => {
					observer.disconnect();
					reject(new Error(`Element not found: ${selector}`));
				}, timeout);
			});
		},

		/**
		 * Align element's top to viewport
		 * @param {HTMLElement} el - Target element
		 * @param {number} offsetPx - Offset from top
		 * @param {boolean} smooth - Use smooth scrolling
		 */
		alignElementToTop(el, offsetPx = 0, smooth = true) {
			const rect = el.getBoundingClientRect();
			const absoluteTop = rect.top + window.pageYOffset;
			const finalTop = Math.max(0, absoluteTop - offsetPx);
			window.scrollTo({ top: finalTop, behavior: smooth ? 'smooth' : 'auto' });
		},

		/**
		 * Initialize scroll functionality
		 */
		init() {
			const href = window.location.href;
			if (!href.includes(this.requiredHash) || href.includes(this.blockedKeyword)) return;

			window.scrollTo(0, 0);

			this.waitForElement(this.targetSelector, 7000)
				.then((el) => {
					requestAnimationFrame(() => this.alignElementToTop(el, this.offset, true));
				})
				.catch((err) => {
					const maybe = document.querySelector(this.targetSelector);
					if (maybe) this.alignElementToTop(maybe, this.offset, true);
					else console.warn(err.message);
				});
		}
	};

	/* ==========================================================================
	   INITIALIZATION
	   ========================================================================== */

	/**
	 * Initialize all components on DOMContentLoaded
	 */
	function initializeComponents() {

		// Announcement bar
		HeaderFundingCounter.init();

		// Header & Navigation
		HeaderSideMenu.init();
		HeaderBlur.init();
		HeaderMarquee.init();

		// Banner
		BannerTopSwiper.init();
		BannerCarousel.init();

		// Milestone
		MilestoneAnimation.init();

		// Swipers
		BePartSwiper.init();
		AboutSwiper.init();
		NewsSwiper.init();
		PodcastSwiper.init();
		GallerySwiper.init();
		ReadMoreSwiper.init();

		// Interactive Components
		FundingCounter.init();
		LearnMoreToggle.init();
		FooterAccordion.init();

		// E-commerce
		CartDrawer.init();
		AjaxAddToCart.init();

		// Search
		SearchModal.init();

		// Utilities
		NewsletterMessage.init();
		ScrollToElement.init();
	}

	/**
	 * Initialize components that need Shopify Theme Editor support
	 * @param {Event} e - Shopify section event
	 */
	function initializeForShopify(e) {
		const section = e?.target;

		BannerTopSwiper.init(section);
		BePartSwiper.init(section);
		GallerySwiper.init(section);

		// Re-init other swipers
		AboutSwiper.init();
		NewsSwiper.init();
		PodcastSwiper.init();
		ReadMoreSwiper.init();
	}

	// DOM Ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initializeComponents);
	} else {
		initializeComponents();
	}

	// Window load fallback
	window.addEventListener('load', () => {
		BannerCarousel.init();
		GallerySwiper.init();
	});

	// Shopify Theme Editor Events
	document.addEventListener('shopify:section:load', initializeForShopify);
	document.addEventListener('shopify:section:reorder', initializeForShopify);
	document.addEventListener('shopify:section:select', initializeForShopify);
	document.addEventListener('shopify:block:select', initializeForShopify);
	document.addEventListener('shopify:block:deselect', initializeForShopify);

	// Resize handler for gallery
	window.addEventListener('resize', () => GallerySwiper.init());

})();


document.addEventListener('DOMContentLoaded', () => {
	const firstSection = document.querySelector('[data-color-scheme]');
	const header = document.querySelector('.header-bottom');

	if (!firstSection || !header) return;

	header.classList.add(`color-${firstSection.dataset.colorScheme}`);

	const learnMoreBtn = document.querySelector(".learn-more-toggle");
	const showLessBtn = document.querySelector(".show-less-toggle");
	const contentMore = document.querySelector(".content-more");
	const dots = document.querySelector(".content-dot");
	const textWrapper = document.querySelector(".image_small .text");

	const learnMoreP = learnMoreBtn.closest("p");

	// 🔹 create inline holder (JS only)
	const inlineHolder = document.createElement("span");
	inlineHolder.className = "inline-content-holder";
	learnMoreP.appendChild(inlineHolder);

	// --------------------
	// Slide Down
	function slideDown(element, duration = 400) {
		element.hidden = false;
		const height = element.scrollHeight;

		element.style.height = "0px";
		element.style.overflow = "hidden";
		element.style.transition = `height ${duration}ms ease-in-out`;

		requestAnimationFrame(() => {
			element.style.height = height + "px";
		});

		setTimeout(() => {
			element.style.height = "";
			element.style.overflow = "";
			element.style.transition = "";
      setTimeout(() => {
        element.classList.add('active');
      }, duration - 300);
		}, duration);
	}

	// --------------------
	// Slide Up
	function slideUp(element, duration = 400) {
		element.style.height = element.scrollHeight + "px";
		element.style.overflow = "hidden";
		element.style.transition = `height ${duration}ms ease-in-out`;

    element.classList.remove('active');

		requestAnimationFrame(() => {
			element.style.height = "0px";
		});

		setTimeout(() => {
			element.hidden = true;
			element.style.height = "";
			element.style.overflow = "";
			element.style.transition = "";
		}, duration);
	}

	// --------------------
	// Learn more click
	learnMoreBtn.addEventListener("click", function (e) {
		e.preventDefault();

		// move content-more inline
		inlineHolder.appendChild(contentMore);

		slideDown(contentMore, 500);

		dots.style.display = "none";
		learnMoreBtn.style.display = "none";

		textWrapper.classList.add("learn-more-open");
		textWrapper.classList.remove("show-less");
	});

	// --------------------
	// Show less click
	showLessBtn.addEventListener("click", function (e) {
		e.preventDefault();

		slideUp(contentMore, 500);

		// move back after animation
		setTimeout(() => {
			textWrapper.appendChild(contentMore);
		}, 500);

		dots.style.display = "inline";
		learnMoreBtn.style.display = "inline";

		textWrapper.classList.remove("learn-more-open");
		textWrapper.classList.add("show-less");
	});

});


document.addEventListener("DOMContentLoaded", function () {
	document.querySelectorAll(".sub-dropdown > a").forEach(link => {
		link.addEventListener("click", function (e) {
			e.preventDefault();

			const parent = this.parentElement;

			// close other open submenus
			parent.parentElement.querySelectorAll(".sub-dropdown").forEach(item => {
				if (item !== parent) {
					item.classList.remove("active");
				}
			});

			parent.classList.toggle("active");
		});
	});


	// coming soon page js 

	new Swiper('.featured-carousel__slider', {
		slidesPerView: 1.2,
		spaceBetween: 16,
		loop: true,
		speed: 500,
		autoplay: {
			delay: 3000,
			disableOnInteraction: false
		},
		pagination: {
			el: '.featured-content-carousel .swiper-pagination',
			clickable: true
		},
		navigation: {
			nextEl: '.featured-button-next',
			prevEl: '.featured-button-prev'
		},
		breakpoints: {
			768: {
				slidesPerView: 2.2
			},
			1024: {
				slidesPerView: 3
			}
		}
	});

});

// gsap animation on page scroll

function initSmoothScroll() {

  if (window.lenis) return; // prevent duplicate

  gsap.registerPlugin(ScrollTrigger);

  window.lenis = new Lenis({
    duration: 1.8,
    smooth: true
  });

  function raf(time) {
    window.lenis.raf(time);
    requestAnimationFrame(raf);
  }

  requestAnimationFrame(raf);

  window.lenis.on('scroll', ScrollTrigger.update);

  gsap.ticker.add((time)=>{
    window.lenis.raf(time * 1000);
  });

  gsap.ticker.lagSmoothing(0);
}


document.addEventListener("DOMContentLoaded", function () {

	if (!document.body.classList.contains("popup-open")) {
		initSmoothScroll();
	}

	gsap.utils.toArray('[data-aos]').forEach(function(elem) {

		let type = elem.getAttribute("data-aos");
		let delay = elem.getAttribute("data-aos-delay") || 0;
		let duration = elem.getAttribute("data-aos-duration") || 1200;
		let offset = elem.getAttribute("data-aos-offset") || 80;
		let once = elem.getAttribute("data-aos-once") === "true";

		let settings = {
		opacity: 0,
		duration: duration / 1000,
		delay: delay / 1000,
		ease: "power4.out"
		};

		// Animation types
		switch(type) {
		case "fade-up":
			settings.y = offset;
			break;

		case "fade-down":
			settings.y = -offset;
			break;

		case "fade-left":
			settings.x = -offset;
			break;

		case "fade-right":
			settings.x = offset;
			break;

		case "zoom-in":
			settings.scale = 0.8;
			break;

		case "zoom-out":
			settings.scale = 1.2;
			break;
		}

		gsap.from(elem, {
		scrollTrigger: {
			trigger: elem,
			start: "top 85%",
			toggleActions: once 
			? "play none none none" 
			: "play none none reverse"
		},
		...settings
		});

	});

});


// SplitText animation

document.addEventListener("DOMContentLoaded", function () {

  if (typeof SplitText === "undefined") {
    console.error("SplitText plugin not loaded.");
    return;
  }

  gsap.registerPlugin(SplitText, ScrollTrigger);

  const targets = document.querySelectorAll(".title h2, .text, .text p, .footer-title h2, .footer-title p, .banner-top-title h1, .banner-top-title p, .text-content p, .content-bottom h3, .content-bottom p, .card-content p, .card-header h4, .card-header p, .funding-bottom p, .funding-bottom a, .bio-bottom-left h3, .bio-bottom-left p, .bio-bottom-left a, .signup-hero__title, .signup-hero__description p, .featured-carousel__title, .featured-carousel__subtitle p, .featured-carousel__sub_heading");

  targets.forEach((el) => {

    // Keep original text for aria-label
    el.setAttribute("aria-label", el.textContent.trim());

    // Split into lines
    const split = new SplitText(el, {
      type: "lines",
      linesClass: "gsap_split_line"
    });

    split.lines.forEach((line, index) => {

      const lineNumber = index + 1;

      // Create mask wrapper (same as reference)
      const mask = document.createElement("div");
      mask.classList.add(
        "gsap_split_line",
        `gsap_split_line${lineNumber}-mask`
      );

      mask.setAttribute("aria-hidden", "true");
      mask.style.position = "relative";
      mask.style.display = "block";
      mask.style.overflow = "clip";
      mask.style.textAlign = getComputedStyle(el).textAlign;

      // Update inner line class
      line.classList.add(`gsap_split_line${lineNumber}`);
      line.setAttribute("aria-hidden", "true");
      line.style.position = "relative";
      line.style.display = "block";

      // Wrap structure
      line.parentNode.insertBefore(mask, line);
      mask.appendChild(line);
    });

    // Animate lines (smooth reveal)
    gsap.from(split.lines, {
      yPercent: 100,
      duration: 1.9,
      ease: "power4.out",
      stagger: 0.12,
      scrollTrigger: {
        trigger: el,
        start: "top 85%",
        toggleActions: "play none none reverse"
      }
    });

  });

  ScrollTrigger.refresh();

});